/**
 * useLocationExecutionStatus Hook
 *
 * Computes location status from recent executions for map visualization.
 * Every tenancy-visible location is a candidate — the org's own locations plus
 * the shared commons — pruned to those with online modems or recent runs.
 * Inactive status for those without recent tests.
 * Status colors: success (green), warning (yellow), error (red), inactive (gray), executing (blue)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useVisibilityAwareInterval } from '@/hooks/useVisibilityAwareInterval';
import { db } from '@/config/firebase';
import { getVisibleDeviceDocs } from '@/lib/visibleDevices';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  Timestamp,
  QuerySnapshot,
  DocumentData
} from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { toDate } from '@/lib/date-utils';
import { filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import type { Country, Location } from '@simcheck/shared-types';
import { isInternalExecution } from '@simcheck/shared-types';

export type LocationStatus = 'success' | 'warning' | 'error' | 'inactive' | 'executing';

export interface LocationExecutionStats {
  locationId: string;
  locationName: string;
  city?: string;
  country?: string;
  countryCode?: string;
  status: LocationStatus;
  totalExecutions24h: number;
  successCount: number;
  failedCount: number;
  runningCount: number;
  successRate: number;
  lastExecutionAt: Date | null;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  modemCount?: number;
  onlineModemCount?: number;
}

export interface LocationStatusColors {
  success: string;
  warning: string;
  error: string;
  inactive: string;
  executing: string;
}

export const STATUS_COLORS: LocationStatusColors = {
  success: '#22c55e',   // green-500
  warning: '#eab308',   // yellow-500
  error: '#ef4444',     // red-500
  inactive: '#6b7280',  // gray-500
  executing: '#3b82f6', // blue-500
};

interface UseLocationExecutionStatusOptions {
  hoursWindow?: number; // Default 24 hours
  refreshInterval?: number; // Auto-refresh interval in ms
}

interface UseLocationExecutionStatusResult {
  locationStats: LocationExecutionStats[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Calculate location status based on execution history
 */
function calculateLocationStatus(
  totalExecutions: number,
  successCount: number,
  failedCount: number,
  runningCount: number
): LocationStatus {
  // Currently executing
  if (runningCount > 0) {
    return 'executing';
  }

  // No recent activity
  if (totalExecutions === 0) {
    return 'inactive';
  }

  // Calculate failure rate
  const failureRate = failedCount / totalExecutions;

  // If latest execution failed or >20% failure rate
  if (failureRate > 0.2) {
    return 'warning';
  }

  // If any failures, show warning
  if (failedCount > 0) {
    return 'warning';
  }

  // All success
  return 'success';
}

/**
 * Hook for computing location execution status for map visualization
 */
export function useLocationExecutionStatus(
  options: UseLocationExecutionStatusOptions = {}
): UseLocationExecutionStatusResult {
  const { activeOrganization, isSuperAdmin } = useAuth();
  const { hoursWindow = 24, refreshInterval } = options;

  const [locationStats, setLocationStats] = useState<LocationExecutionStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track whether the latest fetch is still current (guards against stale async updates)
  const fetchIdRef = useRef(0);

  const loadLocationStats = useCallback(async () => {
    if (!activeOrganization?.id) {
      setLocationStats([]);
      setLoading(false);
      return;
    }

    const fetchId = ++fetchIdRef.current;

    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const startTime = new Date(now.getTime() - hoursWindow * 60 * 60 * 1000);

      // 1. Load reference data (countries, locations) + online devices.
      //    Online devices give live per-location modem counts — the same signal
      //    the Global Locations panel (LocationSummary) uses to decide which
      //    locations to surface, so the map plots the identical set.
      const [countriesSnap, locationsSnap, onlineDevicesSnap] = await Promise.all([
        getDocs(collection(db, 'countries')),
        getDocs(collection(db, 'locations')),
        getVisibleDeviceDocs(db, activeOrganization.id, isSuperAdmin, [where('status', '==', 'online')]),
      ]);

      const countries = countriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Country[];
      // Tenancy (#202): the org's own locations plus the shared commons; never
      // another org's private locations. Clusters are an execution grouping,
      // NOT a visibility filter — scoping the candidate set to them hid the
      // shared locations where a cluster-owning org's tests actually run
      // (a demo org with 5 seeded clusters saw only its own seeded sites).
      const candidateLocations = filterLocationsByTenancy(
        locationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Location[],
        activeOrganization.id,
        isSuperAdmin
      );

      // Live modem counts per location, summed across ONLINE devices only. Offline
      // devices contribute 0, so a location that temporarily drops can still be kept
      // by its recent test runs (below) instead of vanishing.
      const liveModemCountByLocation = new Map<string, number>();
      onlineDevicesSnap.forEach((deviceDoc) => {
        const data = deviceDoc.data() as any;
        const locationId = data.locationId;
        if (!locationId) return;
        const count = Array.isArray(data.modems)
          ? data.modems.length
          : (data.health?.modemCount ?? 0);
        liveModemCountByLocation.set(
          locationId,
          (liveModemCountByLocation.get(locationId) ?? 0) + count
        );
      });

      // Build country lookup map
      const countryMap = new Map<string, { name: string; code: string }>();
      countries.forEach((country: any) => {
        countryMap.set(country.id, { name: country.name, code: country.code || country.isoCode || '' });
      });

      // Enrich locations with country info
      candidateLocations.forEach((location: any) => {
        if (location.countryId && countryMap.has(location.countryId)) {
          const country = countryMap.get(location.countryId)!;
          location.countryName = country.name;
          location.countryCode = country.code;
        }
      });

      // 2. Fetch recent executions for this org — these are what pull in the
      //    SHARED locations the org actually tests at (the org owns none of them)
      const executionsQuery = query(
        collection(db, 'executions'),
        where('organizationId', '==', activeOrganization.id),
        where('startedAt', '>=', Timestamp.fromDate(startTime)),
        orderBy('startedAt', 'desc')
      );
      const executionsSnapshot = await getDocs(executionsQuery);

      // Group executions by location
      const executionsByLocation = new Map<string, DocumentData[]>();
      executionsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Platform-operated runs must not put a location on the tenant's map
        // (that is how another tenant's site leaked into a customer view, #267).
        if (isInternalExecution(data)) return;
        const locationId = data.locationId;
        if (locationId) {
          if (!executionsByLocation.has(locationId)) {
            executionsByLocation.set(locationId, []);
          }
          executionsByLocation.get(locationId)!.push(data);
        }
      });

      // 3. Keep a location only if it currently has online modems OR had
      //    executions in the window — the same rule the Global Locations panel
      //    (LocationSummary) applies, so the map plots the identical set. Not
      //    gated on super-admin (neither is the panel), so it also holds when a
      //    super-admin is "Viewing as" an organization.
      const locations = candidateLocations.filter(loc =>
        (liveModemCountByLocation.get(loc.id) ?? 0) > 0 ||
        (executionsByLocation.get(loc.id)?.length ?? 0) > 0
      );

      // Calculate stats for each location
      const stats: LocationExecutionStats[] = locations.map((location) => {
        const executions = executionsByLocation.get(location.id) || [];
        const successCount = executions.filter((e) => e.status === 'completed').length;
        const failedCount = executions.filter((e) => e.status === 'failed').length;
        const runningCount = executions.filter((e) => e.status === 'running' || e.status === 'pending').length;
        const totalExecutions = executions.length;

        // Get last execution timestamp
        let lastExecutionAt: Date | null = null;
        if (executions.length > 0 && executions[0].startedAt) {
          lastExecutionAt = toDate(executions[0].startedAt) || null;
        }

        const status = calculateLocationStatus(
          totalExecutions,
          successCount,
          failedCount,
          runningCount
        );

        return {
          locationId: location.id,
          locationName: location.name || location.displayName || 'Unknown Location',
          city: location.city,
          country: (location as any).countryName,
          countryCode: (location as any).countryCode,
          status,
          totalExecutions24h: totalExecutions,
          successCount,
          failedCount,
          runningCount,
          successRate: totalExecutions > 0 ? Math.round((successCount / totalExecutions) * 100) : 0,
          lastExecutionAt,
          coordinates: location.coordinates,
          modemCount: location.modemCount,
          onlineModemCount: location.availableModemCount,
        };
      });

      if (fetchId !== fetchIdRef.current) return;
      setLocationStats(stats);
      setLoading(false);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Error loading location execution status:', err);
      setError('Failed to load location status');
      setLoading(false);
    }
  }, [activeOrganization?.id, hoursWindow]);

  // Initial load and organization change
  useEffect(() => {
    loadLocationStats();
  }, [loadLocationStats]);

  // Visibility-aware auto-refresh: pauses when tab is hidden
  useVisibilityAwareInterval(loadLocationStats, refreshInterval || 60000, !!refreshInterval);

  return {
    locationStats,
    loading,
    error,
    refresh: loadLocationStats,
  };
}

/**
 * Get status color for a given location status
 */
export function getStatusColor(status: LocationStatus): string {
  return STATUS_COLORS[status];
}

/**
 * Get status label for display
 */
export function getStatusLabel(status: LocationStatus): string {
  const labels: Record<LocationStatus, string> = {
    success: 'All Tests Passed',
    warning: 'Some Issues',
    error: 'Failures Detected',
    inactive: 'No Recent Activity',
    executing: 'Tests Running',
  };
  return labels[status];
}
